/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useEmployeesStore } from '../../../team/store/useEmployeesStore';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import type { ReportsVentasLayoutMode } from '../reportsVentasLayout';
import { ReportsScopeEntityFilterPicker } from './ReportsScopeEntityFilterPicker';
import { ReportsScopeFilterSheet, ReportsScopeFilterSheetRow } from './ReportsScopeFilterSheet';

type OperatorOption = {
  authUserId: string;
  label: string;
};

export function ReportsOperatorFilterControl({
  vm,
  disabled,
  layout,
}: {
  vm: ReportsScreenViewModel;
  disabled?: boolean;
  layout: ReportsVentasLayoutMode;
}) {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const { entitlements } = usePlanEntitlements();
  const employees = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const employeesLoading = useEmployeesStore((s) => s.employeesLoading);

  const isOwner = entitlements?.is_owner === true;
  const active = vm.reportsOperatorFilterId != null;
  const valueLabel = vm.reportsOperatorFilterLabel ?? 'Operador';

  useEffect(() => {
    if (!open || employees.length > 0 || employeesLoading) return;
    void loadEmployees();
  }, [open, employees.length, employeesLoading, loadEmployees]);

  const options = useMemo((): OperatorOption[] => {
    const out: OperatorOption[] = [];
    const seen = new Set<string>();

    if (user?.id && isOwner) {
      const ownerName = selfOwner?.name?.trim();
      out.push({
        authUserId: user.id,
        label: ownerName && ownerName !== 'Vos' ? `${ownerName} (dueño)` : 'Tu actividad (dueño)',
      });
      seen.add(user.id);
    }

    if (!isOwner && user?.id) {
      const self = employees.find((e) => e.authUserId === user.id);
      return [{ authUserId: user.id, label: self?.name?.trim() || 'Tu actividad' }];
    }

    for (const emp of employees) {
      const id = emp.authUserId?.trim();
      if (!id || seen.has(id) || emp.isActive === false) continue;
      seen.add(id);
      out.push({ authUserId: id, label: emp.name?.trim() || `Empleado ${id.slice(0, 8)}` });
    }

    return out.sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [employees, selfOwner, isOwner, user?.id]);

  const onSelect = useCallback(
    (opt: OperatorOption) => {
      vm.setReportsOperatorFilter(opt.authUserId, opt.label);
      setOpen(false);
    },
    [vm]
  );

  const onClear = useCallback(() => {
    vm.clearReportsOperatorFilter();
    setOpen(false);
  }, [vm]);

  return (
    <>
      <ReportsScopeEntityFilterPicker
        disabled={disabled}
        active={active}
        caption="Operador"
        valueLabel={valueLabel}
        emptyLabel="Todos"
        onPress={() => setOpen(true)}
        layout={layout}
        accessibilityLabel={
          active ? `Operador: ${valueLabel}. Cambiar filtro` : 'Filtrar por operador. Abrir lista'
        }
      />

      <ReportsScopeFilterSheet
        visible={open}
        title="Operador"
        onClose={() => setOpen(false)}
        hint="Ves solo las ventas de esa persona."
      >
        <ReportsScopeFilterSheetRow
          label="Todos los operadores"
          selected={!active}
          onPress={onClear}
          disabled={disabled}
        />
        {options.map((opt) => (
          <ReportsScopeFilterSheetRow
            key={opt.authUserId}
            label={opt.label}
            selected={vm.reportsOperatorFilterId === opt.authUserId}
            onPress={() => onSelect(opt)}
            disabled={disabled}
          />
        ))}
        {options.length === 0 ? (
          <ReportsScopeFilterSheetRow
            label={employeesLoading ? 'Cargando equipo…' : 'No hay operadores para filtrar'}
            selected={false}
            onPress={() => setOpen(false)}
            disabled
          />
        ) : null}
      </ReportsScopeFilterSheet>
    </>
  );
}
